import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

const DrugDeviceData = ({ selectedStudy }) => {
  if (!selectedStudy) return null;

  const settings = selectedStudy.settings || {};
  const metrics = selectedStudy.metrics || {};
  const isDrugData = settings.is_drug === true || settings.is_drug === "true";

  const deviceName =
    settings.deviceName ||
    settings.product_name ||
    selectedStudy.product_name ||
    "—";

  const deviceInfo =
    settings.deviceInfo ||
    settings.product_descrp ||
    "—";

  const intendedUse = settings.intended_use || "—";
  const users = settings.users || settings.users_description || "—";
  const population = settings.population_term || "";
  const intervention = settings.intervention_term || "";
  const comparator = settings.comparator_term || "";
  const outcomes = settings.outcomes_term || "";

  const oldPopulation = settings.population || "";
  const oldIntervention = settings.intervention || "";
  const oldComparator = settings.comparator || "";
  const oldOutcomes = settings.outcomes || settings.outcome || "";

  const createdAt = settings.created || "—";
  const articleType = settings.articleType || "—";
  const abstractFlag =
    typeof settings.abstract === "boolean"
      ? settings.abstract
        ? "Yes"
        : "No"
      : settings.abstract ?? "—";
  const minDate = settings.mindate || "—";
  const maxDate = settings.maxdate || "—";
  const dataPath = settings.device_data_path || "—";

  const metricSummary = [
    {
      label: "Unique PMID Count",
      value: metrics?.Unique_PMID_Count ?? "—",
    },
    {
      label: "Total PMID Occurrences",
      value: metrics?.Total_PMID_Occurrences ?? "—",
    },
    {
      label: "Duplicate PMID Count",
      value: metrics?.Duplicate_PMID_Count ?? "—",
    },
  ];

  const searchTermCounts =
    metrics?.Search_Termwise_Article_Counts || {};
  const searchTermChartData = Object.entries(searchTermCounts).map(
    ([term, count]) => ({
      term,
      count,
    }),
  );

  const perTermMetrics = metrics?.Per_Term_Metrics || {};
  const perTermRows = Object.entries(perTermMetrics).map(
    ([term, data]) => ({
      term,
      all_articles: data?.all_articles ?? "—",
      relevant_articles: data?.relevant_articles ?? "—",
      novel_articles: data?.novel_articles ?? "—",
      claims_relevant_articles:
        data?.claims_relevant_articles ?? "—",
    }),
  );
  const hasMetrics =
    metrics && typeof metrics === "object" && Object.keys(metrics).length > 0;

  return (
    <div className="grid min-h-0 grid-cols-12 gap-4">
      <div className="col-span-12 rounded-xl border border-base-300 bg-base-100 p-5 shadow-sm lg:col-span-6">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-base-content">
            {isDrugData ? "Drug Data" : "Device Data"}
          </h3>
          <span className="badge badge-outline">
            {isDrugData ? "Drug" : "Device"}
          </span>
        </div>

        <div className="mt-4 space-y-4">
          <div>
            <div className="text-sm font-semibold text-base-content/70">
              Name
            </div>
            <div className="text-base font-semibold text-base-content">
              {deviceName}
            </div>
          </div>

          <div>
            <div className="text-sm font-semibold text-base-content/70">
              Description
            </div>
            <p className="mt-1 whitespace-pre-wrap text-sm text-base-content">
              {deviceInfo}
            </p>
          </div>

          <div>
            <div className="text-sm font-semibold text-base-content/70">
              Intended Use
            </div>
            <p className="mt-1 text-sm text-base-content">
              {intendedUse}
            </p>
          </div>

          <div>
            <div className="text-sm font-semibold text-base-content/70">
              Users
            </div>
            <p className="mt-1 text-sm text-base-content">{users}</p>
          </div>

          {(population || intervention || comparator || outcomes) && (
            <div className="rounded-lg border border-base-300 bg-base-200/40 p-3">
              <div className="mb-2 text-xs font-semibold text-base-content/60">
                PICO (Terms)
              </div>
              {population && (
                <div className="mb-2">
                  <div className="text-sm font-semibold text-base-content/70">
                    Population
                  </div>
                  <p className="mt-1 text-sm text-base-content">{population}</p>
                </div>
              )}
              {intervention && (
                <div className="mb-2">
                  <div className="text-sm font-semibold text-base-content/70">
                    Intervention
                  </div>
                  <p className="mt-1 text-sm text-base-content">{intervention}</p>
                </div>
              )}
              {comparator && (
                <div className="mb-2">
                  <div className="text-sm font-semibold text-base-content/70">
                    Comparator
                  </div>
                  <p className="mt-1 text-sm text-base-content">{comparator}</p>
                </div>
              )}
              {outcomes && (
                <div>
                  <div className="text-sm font-semibold text-base-content/70">
                    Outcome
                  </div>
                  <p className="mt-1 text-sm text-base-content">{outcomes}</p>
                </div>
              )}
            </div>
          )}

          {(oldPopulation || oldIntervention || oldComparator || oldOutcomes) && (
            <div className="rounded-lg border border-base-300 bg-base-200/40 p-3">
              <div className="mb-2 text-xs font-semibold text-base-content/60">
                PICO (Original)
              </div>
              {oldPopulation && (
                <div className="mb-2">
                  <div className="text-sm font-semibold text-base-content/70">
                    Population
                  </div>
                  <p className="mt-1 text-sm text-base-content">{oldPopulation}</p>
                </div>
              )}
              {oldIntervention && (
                <div className="mb-2">
                  <div className="text-sm font-semibold text-base-content/70">
                    Intervention
                  </div>
                  <p className="mt-1 text-sm text-base-content">{oldIntervention}</p>
                </div>
              )}
              {oldComparator && (
                <div className="mb-2">
                  <div className="text-sm font-semibold text-base-content/70">
                    Comparator
                  </div>
                  <p className="mt-1 text-sm text-base-content">{oldComparator}</p>
                </div>
              )}
              {oldOutcomes && (
                <div>
                  <div className="text-sm font-semibold text-base-content/70">
                    Outcome
                  </div>
                  <p className="mt-1 text-sm text-base-content">{oldOutcomes}</p>
                </div>
              )}
            </div>
          )}

          <div className="grid grid-cols-1 gap-3 pt-2 text-sm md:grid-cols-2">
            <div>
              <div className="text-xs font-semibold text-base-content/60">
                Created
              </div>
              <div className="text-sm text-base-content">
                {createdAt}
              </div>
            </div>
            <div>
              <div className="text-xs font-semibold text-base-content/60">
                Article Type
              </div>
              <div className="text-sm text-base-content">
                {articleType}
              </div>
            </div>
            <div>
              <div className="text-xs font-semibold text-base-content/60">
                Abstract
              </div>
              <div className="text-sm text-base-content">
                {abstractFlag}
              </div>
            </div>
            <div>
              <div className="text-xs font-semibold text-base-content/60">
                Date Range
              </div>
              <div className="text-sm text-base-content">
                {minDate} - {maxDate}
              </div>
            </div>
            <div className="md:col-span-2">
              {/* <div className="text-xs font-semibold text-base-content/60">
                Data Path
              </div> */}
              <div className="truncate text-sm text-base-content">
                {dataPath}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="col-span-12 rounded-xl border border-base-300 bg-base-100 p-5 shadow-sm lg:col-span-6">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-base-content">
            Metrics
          </h3>
          <span className="badge badge-primary badge-outline">
            Articles
          </span>
        </div>

        {!hasMetrics && (
          <div className="mt-4 rounded-lg border border-base-300 bg-base-200/40 p-4 text-sm text-base-content/70">
            No metrics available.
          </div>
        )}

        {hasMetrics && (
          <>
            <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
              {metricSummary.map((item) => (
                <div
                  key={item.label}
                  className="rounded-lg border border-base-300 bg-base-200/40 p-3"
                >
                  <div className="text-xs font-semibold text-base-content/60">
                    {item.label}
                  </div>
                  <div className="mt-1 text-lg font-semibold text-base-content">
                    {item.value}
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-6">
              <div className="text-sm font-semibold text-base-content/70">
                Search Termwise Article Counts
              </div>
              {searchTermChartData.length > 0 ? (
                <div className="mt-3 h-56 w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={searchTermChartData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="term" />
                      <YAxis />
                      <Tooltip />
                      <Bar dataKey="count" fill="#1d4ed8" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="mt-2 text-sm text-base-content/60">
                  No search term metrics available.
                </div>
              )}
            </div>

            <div className="mt-6">
              <div className="text-sm font-semibold text-base-content/70">
                Per-Term Metrics
              </div>
              {perTermRows.length > 0 ? (
                <div className="mt-3 overflow-x-auto rounded-lg border border-base-300">
                  <table className="table table-zebra w-full text-sm">
                    <thead>
                      <tr className="bg-base-200 text-base-content">
                        <th>Search Term</th>
                        <th>All Articles</th>
                        <th>Relevant</th>
                        <th>Novel</th>
                        <th>Claims Relevant</th>
                      </tr>
                    </thead>
                    <tbody>
                      {perTermRows.map((row) => (
                        <tr key={row.term}>
                          <td className="font-medium">{row.term}</td>
                          <td>{row.all_articles}</td>
                          <td>{row.relevant_articles}</td>
                          <td>{row.novel_articles}</td>
                          <td>{row.claims_relevant_articles}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="mt-2 text-sm text-base-content/60">
                  No per-term metrics available.
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default DrugDeviceData;
